import { CreatePromptRulesRequest } from '@shared/models/promptRules'
import { FocusState, RulesState, TermsState } from '../types'
import { CONSTANTS, parseRules } from '../utils/index'

export function createPromptRulesPayload(
  companyID: string,
  brandID: string,
  rules: RulesState,
  termsState: TermsState,
  focusState: FocusState,
  excludedFavorabilityDataSources: number[] = [],
): CreatePromptRulesRequest {
  const currentBrandRules = rules.brands[brandID]
  const currentBrandTerms = termsState[brandID]
  const currentBrandFocus = focusState[brandID]

  if (!currentBrandRules || !currentBrandTerms || !currentBrandFocus) {
    throw new Error(`Brand data not found for brandId: ${brandID}`)
  }

  const createRuleGroup = (
    polarity: 'POSITIVO' | 'NEGATIVO' | 'FAVORAVEL' | 'DESFAVORAVEL' | 'POSITIVO_FAVORAVEL' | 'NEGATIVO_DESFAVORAVEL',
    isGeneral: boolean,
    dataSourceID: number,
  ) => {
    const isSocial = dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID
    const polarityKey =
      polarity === 'POSITIVO' ? 'Positive'
        : polarity === 'NEGATIVO' ? 'Negative'
          : polarity === 'FAVORAVEL' ? 'Favorable'
            : polarity === 'DESFAVORAVEL' ? 'Unfavorable'
              : polarity === 'POSITIVO_FAVORAVEL' ? 'PositiveFavorable'
                : 'NegativeUnfavorable'

    const ruleGroup: any = {
      polarity,
      isGeneral,
      dataSourceID,
      rules: [],
      proprietaryTerms: [],
    }

    if (polarity === 'POSITIVO_FAVORAVEL' || polarity === 'NEGATIVO_DESFAVORAVEL') {
      if (isSocial) {
        const publishersKey = `social${polarityKey}PublishersTags` as keyof TermsState[string]
        const proprietaryKey = `social${polarityKey}ProprietaryTags` as keyof TermsState[string]

        ruleGroup.publishers = currentBrandTerms[publishersKey].map((tag) => tag.text)
        ruleGroup.proprietaryTerms = currentBrandTerms[proprietaryKey].map((tag) => tag.text)
      } else {
        const channelsKey = `press${polarityKey}ChannelsTags` as keyof TermsState[string]
        const proprietaryKey = `press${polarityKey}ProprietaryTags` as keyof TermsState[string]

        ruleGroup.channels = currentBrandTerms[channelsKey].map((tag) => tag.text)
        ruleGroup.proprietaryTerms = currentBrandTerms[proprietaryKey].map((tag) => tag.text)
      }
      return ruleGroup
    }

    const rulesKey = isGeneral ? `${polarityKey.toLowerCase()}General` : `${polarityKey.toLowerCase()}Specific`
    const rulesText =
      isSocial ?
        currentBrandRules.social[rulesKey as keyof typeof currentBrandRules.social]
        : currentBrandRules.press[rulesKey as keyof typeof currentBrandRules.press]

    ruleGroup.rules = parseRules(rulesText)

    if (isSocial) {
      const publishersKey = `social${polarityKey}PublishersTags` as keyof TermsState[string]
      const proprietaryKey = `social${polarityKey}ProprietaryTags` as keyof TermsState[string]

      ruleGroup.publishers = currentBrandTerms[publishersKey].map((tag) => tag.text)
      ruleGroup.proprietaryTerms = currentBrandTerms[proprietaryKey].map((tag) => tag.text)
    } else {
      const channelsKey = `press${polarityKey}ChannelsTags` as keyof TermsState[string]
      const proprietaryKey = `press${polarityKey}ProprietaryTags` as keyof TermsState[string]

      ruleGroup.channels = currentBrandTerms[channelsKey].map((tag) => tag.text)
      ruleGroup.proprietaryTerms = currentBrandTerms[proprietaryKey].map((tag) => tag.text)
    }

    return ruleGroup
  }

  const priorityRules: any[] = []

  if (rules.global.priorityFirstStage.trim()) {
    priorityRules.push({
      polarity: 'COMPLEMENT_PROMPT_1',
      isGeneral: false,
      dataSourceID: CONSTANTS.SOCIAL_MEDIA_DS_ID,
      rules: parseRules(rules.global.priorityFirstStage.trim()),
    })
    priorityRules.push({
      polarity: 'COMPLEMENT_PROMPT_1',
      isGeneral: false,
      dataSourceID: CONSTANTS.PRESS_DS_ID,
      rules: parseRules(rules.global.priorityFirstStage.trim()),
    })
  }

  if (rules.global.prioritySecondStage.trim()) {
    priorityRules.push({
      polarity: 'COMPLEMENT_PROMPT_2',
      isGeneral: false,
      dataSourceID: CONSTANTS.SOCIAL_MEDIA_DS_ID,
      rules: parseRules(rules.global.prioritySecondStage.trim()),
    })
    priorityRules.push({
      polarity: 'COMPLEMENT_PROMPT_2',
      isGeneral: false,
      dataSourceID: CONSTANTS.PRESS_DS_ID,
      rules: parseRules(rules.global.prioritySecondStage.trim()),
    })
  }

  return {
    companyID,
    brandID,
    globalRules: {
      rules: parseRules(rules.global.contextRules),
    },
    socialClientFocus: currentBrandFocus.socialClientFocusTags.map((tag) => tag.text),
    socialCompetitorFocus: currentBrandFocus.socialCompetitorFocusTags.map((tag) => tag.text),
    pressClientFocus: currentBrandFocus.pressClientFocusTags.map((tag) => tag.text),
    pressCompetitorFocus: currentBrandFocus.pressCompetitorFocusTags.map((tag) => tag.text),
    excludedFavorabilityDataSources,
    rules: [
      createRuleGroup('POSITIVO', true, CONSTANTS.SOCIAL_MEDIA_DS_ID),
      createRuleGroup('POSITIVO', false, CONSTANTS.SOCIAL_MEDIA_DS_ID),
      createRuleGroup('NEGATIVO', true, CONSTANTS.SOCIAL_MEDIA_DS_ID),
      createRuleGroup('NEGATIVO', false, CONSTANTS.SOCIAL_MEDIA_DS_ID),
      createRuleGroup('FAVORAVEL', true, CONSTANTS.SOCIAL_MEDIA_DS_ID),
      createRuleGroup('FAVORAVEL', false, CONSTANTS.SOCIAL_MEDIA_DS_ID),
      createRuleGroup('DESFAVORAVEL', true, CONSTANTS.SOCIAL_MEDIA_DS_ID),
      createRuleGroup('DESFAVORAVEL', false, CONSTANTS.SOCIAL_MEDIA_DS_ID),
      createRuleGroup('POSITIVO', true, CONSTANTS.PRESS_DS_ID),
      createRuleGroup('POSITIVO', false, CONSTANTS.PRESS_DS_ID),
      createRuleGroup('NEGATIVO', true, CONSTANTS.PRESS_DS_ID),
      createRuleGroup('NEGATIVO', false, CONSTANTS.PRESS_DS_ID),
      createRuleGroup('FAVORAVEL', true, CONSTANTS.PRESS_DS_ID),
      createRuleGroup('FAVORAVEL', false, CONSTANTS.PRESS_DS_ID),
      createRuleGroup('DESFAVORAVEL', true, CONSTANTS.PRESS_DS_ID),
      createRuleGroup('DESFAVORAVEL', false, CONSTANTS.PRESS_DS_ID),
      createRuleGroup('POSITIVO_FAVORAVEL', false, CONSTANTS.SOCIAL_MEDIA_DS_ID),
      createRuleGroup('NEGATIVO_DESFAVORAVEL', false, CONSTANTS.SOCIAL_MEDIA_DS_ID),
      createRuleGroup('POSITIVO_FAVORAVEL', false, CONSTANTS.PRESS_DS_ID),
      createRuleGroup('NEGATIVO_DESFAVORAVEL', false, CONSTANTS.PRESS_DS_ID),
      {
        polarity: 'NEUTRO',
        isGeneral: false,
        dataSourceID: CONSTANTS.SOCIAL_MEDIA_DS_ID,
        rules: parseRules(currentBrandRules.social.neutralRules),
      },
      {
        polarity: 'NEUTRO',
        isGeneral: false,
        dataSourceID: CONSTANTS.PRESS_DS_ID,
        rules: parseRules(currentBrandRules.press.neutralRules),
      },
      ...priorityRules,
    ].filter(
      (ruleGroup) =>
        ruleGroup.rules.length > 0 ||
        ruleGroup.publishers?.length > 0 ||
        ruleGroup.channels?.length > 0 ||
        ruleGroup.proprietaryTerms?.length > 0,
    ),
  }
}
